import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
// 2026-09-24 (Jaime ruling): create-your-unit nudge for home-less users.
import CreateHomeNudge from '../components/CreateHomeNudge';
import { track } from '../services/analyticsTracker';
import { Plus, Check, Trash2, Edit2, X, Calendar as CalendarIcon, Flag, Loader2, AlertCircle, Repeat as RepeatIcon, ChevronLeft, ChevronRight, StickyNote, ImagePlus, MoreVertical, Copy, RefreshCw } from 'lucide-react';
import { homeApi } from '../services/homeApi';
// RULING (Jaime, 2026-09-22): note pictures are compressed client-side before
// upload (same ~150-400KB target as other app images) via the shared helper.
import { compressImageToDataUrl } from '../utils/imageCompress';
import { unitsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useActiveHomeUnitId } from '../hooks/useActiveHomeUnitId';
import { useEntitlements } from '../hooks/useEntitlements';
import { homeModuleEntitled } from '../utils/homeEntitlementGate';
// Beacon lane (2026-10-01): silent empty-200 diagnostic (TH1 class).
import { beaconEmptySuccess } from '../lib/homeBeacons';
import { UnitPrompt } from '../components/HomeStateBanners';
import { isEmployeeContextForLens } from '../config/navigation';
import Avatar from '../components/Avatar';

interface Task {
  id: string;
  title: string;
  category: string;
  assignedTo?: string;
  // FIX (2026-09-01): list rows come straight from home_tasks SELECT * —
  // snake_case. assignedTo (camel) is only what this page sends on POST/PUT.
  assigned_to?: string;
  // FEATURE (2026-09-01, Jaime ruling): photo avatar on assignee chips when the
  // backend starts returning it on task rows; initials fallback until then.
  avatarUrl?: string | null;
  avatar_url?: string | null;
  // FEATURE (2026-09-01): dueDate optional (plain to-do when null), accepts
  // ISO datetime or date-only; repeat/series fields per migration 95 backend.
  dueDate?: string | null;
  endDate?: string | null;
  repeatRule?: any | null;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in_progress' | 'completed';
  seriesId?: string | null;
  recurrenceParentId?: string | null;
  completedAt?: string | null;
  archivedAt?: string | null;
  // snake_case variants (list rows come straight from home_tasks SELECT *)
  due_date?: string | null;
  end_date?: string | null;
  repeat_rule?: any | null;
  series_id?: string | null;
  recurrence_parent_id?: string | null;
  completed_at?: string | null;
  archived_at?: string | null;
  // FIX (2026-08-31): creator/assignee ids come straight from home_tasks rows
  // (SELECT * -> snake_case). user_id is the CREATOR; assignee_user_id the
  // assignee. camelCase variants kept defensive in case of future serialization.
  user_id?: string;
  userId?: string;
  created_by?: string;
  createdBy?: string;
  assignee_user_id?: string;
  assigneeUserId?: string;
  // FEATURE (2026-09-22, Jaime scope correction): per-task Nota + optional
  // picture. GET/list rows return note_content / note_image_path / imageUrl
  // (snake_case rows straight from home_tasks SELECT *); the task PUT accepts
  // noteContent (<=2500 chars, 400 when over) + noteImage (data URL, same
  // base64 pipeline as the former notes API).
  noteContent?: string | null;
  note_content?: string | null;
  noteImagePath?: string | null;
  note_image_path?: string | null;
  imageUrl?: string | null;
}

// FEATURE (2026-09-01, Jaime): view tabs instead of status chips.
// Pendientes (default) | Completadas | Todas. Pending view hides completed
// (and defensively archived) tasks; sorting still puts completed last in Todas.
type ViewTab = 'pendientes' | 'completadas' | 'todas';

// Feature (2026-08-31, Jaime): "Asignar a..." dropdown populated from the unit's
// members. Source: GET /units/:id -> unit.residents (active user_condo_roles rows
// joined to users: owner, residents, family, employees assigned to the unit),
// same data source used by StaffDirectory for standalone units. Deduped by user.
interface UnitMember {
  userId: string;
  name: string;
  role: string;
}

const memberRoleLabels: Record<string, string> = {
  propietario: 'Propietario',
  owner: 'Propietario',
  residente: 'Residente',
  resident: 'Residente',
  familiar: 'Familiar',
  empleado: 'Personal',
  personal: 'Personal',
};

const memberRoleLabel = (role?: string) =>
  memberRoleLabels[String(role || '').toLowerCase()] || String(role || '').trim();

const categoryColors: Record<string, string> = {
  cleaning: 'bg-green-100 text-green-700',
  maintenance: 'bg-orange-100 text-orange-700',
  shopping: 'bg-blue-100 text-blue-700',
  other: 'bg-[var(--v-surface-2)] text-gray-700',
};

const categoryLabels: Record<string, string> = {
  cleaning: 'Limpieza',
  maintenance: 'Mantenimiento',
  shopping: 'Compras',
  other: 'Otro',
};

const priorityColors: Record<string, string> = {
  low: 'text-gray-400',
  medium: 'text-yellow-500 dark:text-yellow-400',
  high: 'text-red-600 dark:text-red-400',
};

const priorityLabels: Record<string, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
};

// FEATURE (2026-09-01, Jaime): repeat ("Repetir") option for tasks.
type RepeatFreq = 'none' | 'daily' | 'weekly' | 'custom';

const weekdayLabels = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

// Form-level repeat state (kept separate from the payload shape).
interface RepeatForm {
  freq: RepeatFreq;
  interval: string; // custom: every N days
  weekdays: number[]; // weekly
  endDate: string; // optional repeat end (date-only)
}

const emptyRepeat: RepeatForm = { freq: 'none', interval: '1', weekdays: [], endDate: '' };

// FEATURE (2026-09-01, Jaime): shared weekday chips (D L M X J V S). Used for
// 'Semanal' (required, at least one day) and 'Cada N días' (optional filter).
const WeekdayPicker = ({ selected, onToggle }: { selected: number[]; onToggle: (d: number) => void }) => (
  <div className="flex gap-1">
    {weekdayLabels.map((lbl, i) => (
      <button
        key={i}
        type="button"
        onClick={() => onToggle(i)}
        className={`w-8 h-8 rounded-full text-sm font-medium ${
          selected.includes(i) ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
        }`}
      >
        {lbl}
      </button>
    ))}
  </div>
);

// Build the server-side repeatRule JSON from form state. Returns null when
// repeat is off. Mirrors backend validateRepeatRule (tasks.routes.js):
// { frequency: daily|weekly|custom, interval?, weekdays?, end_date? }.
const buildRepeatRule = (r: RepeatForm) => {
  if (r.freq === 'none') return null;
  const rule: any = { frequency: r.freq };
  if (r.freq === 'custom') {
    const n = parseInt(r.interval, 10);
    rule.interval = Number.isInteger(n) && n > 0 ? n : 1;
    // Optional weekday filter for 'Cada N días' (e.g. every 2 days on Mon/Fri).
    const weekdays = [...r.weekdays].sort((a, b) => a - b);
    if (weekdays.length > 0) rule.weekdays = weekdays;
  }
  if (r.freq === 'weekly') {
    const weekdays = [...r.weekdays].sort((a, b) => a - b);
    if (weekdays.length === 0) return null; // need at least one weekday
    rule.weekdays = weekdays;
  }
  if (r.endDate) rule.end_date = r.endDate;
  return rule;
};

const parseRepeatRule = (raw: any): RepeatForm => {
  const base: RepeatForm = { ...emptyRepeat };
  if (!raw) return base;
  let rule = raw;
  if (typeof raw === 'string') {
    try { rule = JSON.parse(raw); } catch { return base; }
  }
  if (!rule || typeof rule !== 'object') return base;
  const freq = String(rule.frequency || rule.freq || '');
  if (freq === 'daily' || freq === 'weekly' || freq === 'custom') base.freq = freq;
  if (freq === 'custom') base.interval = String(rule.interval ?? 1);
  if ((freq === 'weekly' || freq === 'custom') && Array.isArray(rule.weekdays)) base.weekdays = rule.weekdays.map(Number);
  const rawEnd = rule.end_date || rule.endDate || rule.end;
  if (rawEnd) {
    const d = new Date(rawEnd);
    if (!isNaN(d.getTime())) base.endDate = d.toISOString().split('T')[0];
  }
  return base;
};

// Normalize due/end fields that may be snake_case/camelCase, ISO datetime or
// date-only. Returns null for plain to-dos (no date).
const taskDue = (t: Task) => t.dueDate ?? t.due_date ?? null;
const taskEnd = (t: Task) => t.endDate ?? t.end_date ?? null;
const taskRepeat = (t: Task) => t.repeatRule ?? t.repeat_rule ?? null;
const taskSeries = (t: Task) => t.seriesId ?? t.series_id ?? t.recurrenceParentId ?? t.recurrence_parent_id ?? null;
const taskCompletedAt = (t: Task) => t.completedAt ?? t.completed_at ?? null;

// FEATURE (2026-09-22, Jaime): per-task note accessors (defensive camel/snake).
const NOTE_MAX_LEN = 2500;
const NOTE_WARN_LEN = 2400;
const taskNote = (t: Task) => t.noteContent ?? t.note_content ?? '';
// imageUrl is the server-rendered URL; note_image_path is the raw stored path
// (fallback for older rows).
const taskNoteImage = (t: Task) => t.imageUrl ?? t.noteImagePath ?? t.note_image_path ?? null;

// ES-locale date formatting for cards: "28 jun 2026" plus ", 14:30" when the
// source value carries a time component.
const fmtDate = new Intl.DateTimeFormat('es', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtTime = new Intl.DateTimeFormat('es', { hour: '2-digit', minute: '2-digit' });
const formatDueDate = (raw?: string | null) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return String(raw);
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(raw));
  // Date-only strings arrive as UTC midnight — render the calendar date as-is.
  const label = dateOnly
    ? fmtDate.format(new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
    : fmtDate.format(d);
  const hasTime = !dateOnly && (d.getHours() !== 0 || d.getMinutes() !== 0);
  return hasTime ? `${label}, ${fmtTime.format(d)}` : label;
};

// FEATURE (2026-09-01, Jaime ruling): shared <Avatar> (photo when the task
// data carries an avatarUrl, deterministic-color initials otherwise). The
// former local initials()/avatarColor() helpers moved to components/Avatar.tsx.

// Sort: completed tasks always last; then dated tasks first (closest upcoming
// first), undated (plain to-dos) after. (2026-09-01, Jaime's ruling.)
const sortByDue = (a: Task, b: Task) => {
  const ca = a.status === 'completed' ? 1 : 0;
  const cb = b.status === 'completed' ? 1 : 0;
  if (ca !== cb) return ca - cb;
  const da = taskDue(a);
  const db = taskDue(b);
  const ta = da ? new Date(da).getTime() : NaN;
  const tb = db ? new Date(db).getTime() : NaN;
  if (isNaN(ta) && isNaN(tb)) return 0;
  if (isNaN(ta)) return 1;
  if (isNaN(tb)) return -1;
  return ta - tb;
};

// FEATURE (2026-09-01, Jaime ruling): grouped ordering in Pendientes/Todas —
//   a) Hoy (due today or overdue), b) Sin fecha, c) Próximas (future dates),
//   then completed tasks last (Todas only). Date-only dues are interpreted in
//   UTC calendar terms (same convention as formatDueDate).
const dueTs = (t: Task): number | null => {
  const raw = taskDue(t);
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(raw))) {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()).getTime();
  }
  return d.getTime();
};
const endOfTodayTs = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.getTime();
};
const byDueTs = (a: Task, b: Task) => {
  const ta = dueTs(a);
  const tb = dueTs(b);
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;
  return ta - tb;
};
type TaskRow = { task: Task; header: string };
const withHeaders = (groups: [string, Task[]][]): TaskRow[] =>
  groups
    .filter(([, items]) => items.length > 0)
    .flatMap(([header, items]) => items.map((task, i) => ({ task, header: i === 0 ? header : '' })));

const emptyForm = {
  title: '',
  category: 'other',
  assignedTo: '',
  priority: 'medium' as Task['priority'],
  status: 'pending' as Task['status'],
  // FEATURE (2026-09-22, Jaime): optional Nota on create/edit. noteImage holds
  // a NEW data URL; noteImagePath echoes the stored filename on edit ('' = clear).
  noteContent: '',
  noteImage: '',
  noteImagePath: null as string | null,
};

// FEATURE (2026-09-01): due date is opt-in via checkbox. When unchecked the
// task is a plain to-do (dueDate=null). Date is required when checked; time
// optional. End date optional. Repeat requires a due date (server needs an
// anchor to compute the next occurrence).
const emptyDue = { hasDue: false, date: '', time: '', endDate: '', endTime: '' };


// FEATURE (2026-09-01, Jaime): Calendario view — monthly month-grid helpers.
type DisplayMode = 'lista' | 'dia' | 'calendario';
const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const CAL_WEEKDAYS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const pad2 = (n: number) => String(n).padStart(2, '0');
const todayKey = () => { const n = new Date(); return `${n.getFullYear()}-${pad2(n.getMonth() + 1)}-${pad2(n.getDate())}`; };

// Calendar day key for a task; null = not plottable (no/parsable due date).
// Date-only dues use the UTC calendar date (same convention as dueTs /
// formatDueDate); datetimes use the local calendar date.
const dateKeyOf = (t: Task): string | null => {
  const raw = taskDue(t);
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(raw))) return String(raw).slice(0, 10);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// 6-week (42-cell) Sunday-start grid covering `month` (0-based).
const monthGrid = (year: number, month: number) => {
  const tk = todayKey();
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  const cells: { key: string; day: number; inMonth: boolean; isToday: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const key = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    cells.push({ key, day: d.getDate(), inMonth: d.getMonth() === month, isToday: key === tk });
  }
  return cells;
};

// FEATURE (2026-09-01, Jaime): Día view helpers.
const fmtWeekday = new Intl.DateTimeFormat('es', { weekday: 'long' });
const dateKeyShift = (key: string, delta: number) => {
  const d = new Date(key + 'T12:00:00');
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
// Local hour of a datetime due; null for date-only/unparsable dues ("Todo el día").
const hourOf = (t: Task): number | null => {
  const raw = taskDue(t);
  if (!raw || /^\d{4}-\d{2}-\d{2}$/.test(String(raw))) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  return d.getHours();
};

export default function HomeTasks() {
  const { user, ownedUnits, activeOwnedUnitId } = useAuth();
  // Active unit object derived the same way MobileMenu does it (shared helper input).
  const activeUnit = (ownedUnits || []).find((u) => u.id === activeOwnedUnitId);
  // FIX (2026-08-31, Jaime bug): employees saw Edit/Delete on tasks they did
  // not create and the backend then 403'd the request. UI now gates on
  // ownership. Employee context reuses the shared lens-scoped helper
  // isEmployeeContextForLens (same signal the desktop/mobile menus use).
  // FIX (Jaime bug 2026-09-09 PM): lens-scoped gate — this is a HOME-lens
  // page, so the employee restrictions apply only to employee housing
  // (activeUnit.assigned). An own standalone home is never an employee
  // context, even when the user's global role or another condo membership is
  // 'empleado'. Mirrors navigation.isEmployeeContextForLens('home', ...).
  const employeeContext = isEmployeeContextForLens('home', user, ownedUnits || [], activeUnit);
  const currentUserId = String(user?.id ?? '');
  // FIX (2026-08-31, Jaime bug): explicit active-unit scoping + refetch on unit change.
  const activeUnitId = useActiveHomeUnitId();
  // fix-173c: entitlement gate — usuariosOnly / home-* stripped payload means
  // NO /api/home/tasks call at all (no 403 banner; neutral empty state).
  const { entitlement, loading: entitlementLoading } = useEntitlements();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewTab>('pendientes');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  // FEATURE (2026-09-01): optional due date + repeat form state.
  const [due, setDue] = useState({ ...emptyDue });
  const [repeat, setRepeat] = useState<RepeatForm>({ ...emptyRepeat });
  // FEATURE (2026-09-01): series editing — when the task belongs to a series,
  // the Save button first asks "Solo esta" vs "Todas" (scope=single|series).
  // '' is the transient "prompt visible, not yet chosen" marker.
  const [pendingScope, setPendingScope] = useState<null | '' | 'single' | 'series'>(null);
  const editingTaskRef = useRef<Task | null>(null);
  const [unitMembers, setUnitMembers] = useState<UnitMember[]>([]);
  // FEATURE (2026-09-01, Jaime): Calendario view state — Lista (grouped list,
  // unchanged) vs Calendario (monthly grid, default month = current).
  const [displayMode, setDisplayMode] = useState<DisplayMode>('lista');
  const nowT = new Date();
  const [calYear, setCalYear] = useState(nowT.getFullYear());
  const [calMonth, setCalMonth] = useState(nowT.getMonth());
  // Day popover: date key ('YYYY-MM-DD') of the day whose full task list is shown.
  const [dayPanel, setDayPanel] = useState<string | null>(null);
  // "Sin fecha / sin hora" panel collapsed state (mobile only; desktop fixed).
  // FIX (2026-09-01, Jaime): panel now starts UNFOLDED (default open); the
  // collapse toggle still works.
  const [undatedOpen, setUndatedOpen] = useState(true);
  // FEATURE (2026-09-01, Jaime): Día view — currently displayed day key.
  const [dayDate, setDayDate] = useState<string>(todayKey());

  // Feature (2026-08-31, Jaime): load unit members for the "Asignar a..." dropdown.
  const fetchUnitMembers = useCallback(async () => {
    if (!activeUnitId) { setUnitMembers([]); return; }
    try {
      const res = await unitsAPI.getResidents(String(activeUnitId));
      const payload = res.data?.data ?? res.data;
      const unit = payload?.unit ?? payload;
      const residents: any[] = Array.isArray(unit?.residents) ? unit.residents : [];
      const seen = new Set<string>();
      const members: UnitMember[] = [];
      for (const r of residents) {
        const userId = String(r.user_id ?? r.userId ?? '');
        const name = String(r.name ?? '').trim();
        if (!userId || !name || seen.has(userId)) continue;
        seen.add(userId);
        members.push({ userId, name, role: String(r.role ?? '') });
      }
      setUnitMembers(members);
    } catch (err: any) {
      if (err?.response?.status !== 401) {
        console.error('[HomeTasks] Failed to load unit members:', err?.message);
      }
      setUnitMembers([]);
    }
  }, [activeUnitId]);

  useEffect(() => { fetchUnitMembers(); }, [fetchUnitMembers]);

  const fetchTasks = useCallback(async () => {
    // FIX LANE #166 (2026-09-24): mirror the Dashboard2.tsx guard — a multi-unit
    // owner with no selected unit would otherwise hit `400 unitId is required`
    // (requireUnit fail-closed rule) on every mount + 6s poll. Skip the API
    // call and show a neutral "select a unit" state instead.
    if (!activeUnitId) { setTasks([]); setError(null); setLoading(false); return; }
    // fix-1004 (BUG-B, QA bugfix-cycle-1004-0400): entitlement fail-open edge,
    // same root cause as BUG-A in HomeShopping. Wait while the payload is
    // loading (usuariosOnly must not hit /api/home/*); if it never arrives
    // (fetch error / no cached value) fail OPEN per homeEntitlementGate.ts's
    // documented rule — the backend remains the authoritative 403 guard. The
    // previous `!entitlementReady` check failed closed forever whenever the
    // payload was unavailable, so entitled users never fetched the list.
    if (!entitlement && entitlementLoading) { return; }
    if (entitlement && !homeModuleEntitled(entitlement, 'home-tasks')) { setTasks([]); setError(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await homeApi.tasks.list(activeUnitId);
      const payload = res.data?.data ?? res.data;
      const tasks = payload?.tasks || payload?.data?.tasks || (Array.isArray(payload) ? payload : []);
      setTasks(Array.isArray(tasks) ? tasks : []);
      // Beacon: 200 with an empty list → session-deduped empty_success report.
      beaconEmptySuccess({ route: '/tasks', unitId: activeUnitId, rowCount: Array.isArray(tasks) ? tasks.length : 0 });
    } catch (err: any) {
      // 401 errors from non-auth endpoints should not show error messages —
      // the axios interceptor handles auth redirects silently.
      if (err?.response?.status !== 401) {
        setError(err?.response?.data?.message || err?.message || 'Error al cargar tareas');
      }
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [activeUnitId, entitlement, entitlementLoading]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // POLLING v1 (2026-08-31, Jaime): pragmatic ~6s refresh while this screen is
  // mounted so tasks created/edited/completed/deleted by other users of the
  // same unit appear without a manual reload. Mirrors the HomeShopping.tsx
  // polling v1 pattern (websocket layer can replace it later). Rules:
  //   - tick only when document.visibilityState === 'visible' (paused on
  //     hidden tabs; an immediate catch-up poll fires on visibilitychange)
  //   - skipped while the inline edit / add form modal is open so we never
  //     clobber someone mid-edit
  //   - silent: a transient poll error must not replace the visible list
  //   - interval is re-created when activeUnitId changes so no tick ever
  //     refetches a stale unit's list (same fix as HomeShopping)
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (editingId || showForm) return;
      if (!activeUnitId) return; // FIX LANE #166: no selected unit → no API call
      // fix-173c: entitlement gate — no poll when home-tasks is not entitled.
      if (!entitlement || !homeModuleEntitled(entitlement, 'home-tasks')) return;
      try {
        const res = await homeApi.tasks.list(activeUnitId);
        if (cancelled) return;
        const payload = res.data?.data ?? res.data;
        const tasks = payload?.tasks || payload?.data?.tasks || (Array.isArray(payload) ? payload : []);
        if (!Array.isArray(tasks)) return;
        setTasks(tasks);
        setError(null);
      } catch {
        // Silent: a transient poll error must not replace the visible list.
      }
    };
    const t = setInterval(poll, 6000);
    const onVis = () => { if (document.visibilityState === 'visible') void poll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [editingId, showForm, activeUnitId, entitlement]);

  // FEATURE (2026-09-01): build the API payload from the form. dueDate is
  // optional (null = plain to-do), ISO datetime when a time is set, date-only
  // otherwise. repeatRule is included only when configured.
  const buildPayload = () => {
    const payload: any = { ...form };
    if (due.hasDue && due.date) {
      payload.dueDate = due.time ? `${due.date}T${due.time}:00` : due.date;
    } else {
      payload.dueDate = null;
    }
    // FEATURE (2026-09-01 15:28): endDate accepts an optional end time — ISO
    // datetime (HH:mm) when set, date-only otherwise. Backend validates it as
    // a date/datetime and passes it through for create and series updates.
    payload.endDate =
      due.hasDue && due.endDate
        ? due.endTime
          ? `${due.endDate}T${due.endTime}:00`
          : due.endDate
        : null;
    const rule = due.hasDue ? buildRepeatRule(repeat) : null;
    if (rule) payload.repeatRule = rule;
    else payload.repeatRule = null;
    // FEATURE (2026-09-22, Jaime): optional Nota on create/edit — same API
    // contract as the per-card editor. POST /home/tasks accepts noteContent +
    // noteImage (data URL); PUT additionally accepts noteImagePath (stored
    // filename to keep, '' to clear).
    payload.noteContent = String(form.noteContent || '').slice(0, NOTE_MAX_LEN);
    if (form.noteImage) payload.noteImage = form.noteImage;
    else if (editingId) payload.noteImagePath = form.noteImagePath ?? '';
    return payload;
  };

  const handleSubmit = async (scope?: 'single' | 'series') => {
    if (!form.title.trim()) return;
    try {
      if (editingId) {
        // Series membership: first save attempt on a series task prompts for
        // scope before the request goes out (status changes are always
        // per-occurrence, so the toggle only affects field edits).
        const editing = editingTaskRef.current;
        if (!scope && editing && taskSeries(editing)) {
          setPendingScope(''); // marker: show Solo esta / Todas choice
          return; // UI now shows the scope choice; buttons re-call handleSubmit
        }
        const body: any = buildPayload();
        if (scope) body.scope = scope;
        await homeApi.tasks.update(editingId, body, activeUnitId);
      } else {
        await homeApi.tasks.create(buildPayload(), activeUnitId);
        // Onboarding checklist (Jaime GO 2026-10-01): a real task completes the step.
        window.dispatchEvent(new CustomEvent('vecindus:onboarding-done', { detail: 'task' }));
        track('tasks_task_created');
      }
      setShowForm(false);
      setEditingId(null);
      editingTaskRef.current = null;
      setForm(emptyForm);
      setDue({ ...emptyDue });
      setRepeat({ ...emptyRepeat });
      setPendingScope(null);
      fetchTasks();
    } catch (err: any) {
      if (err?.response?.status !== 401) {
        // FIX (2026-09-01, Jaime ruling): backend error bodies carry the reason
        // in `error` (not `message`) — reading only `message` let a rejected
        // save (e.g. 403 Not authorized / TASK_EMPLOYEE_LIMITED) fall through
        // to a generic axios message or vanish, the same swallow as the delete
        // bug fixed in deleteErrorMessage. Mirror that extraction here.
        // The save did NOT happen: keep the form open so the error is visible
        // next to the edits instead of a silently unchanged list.
        const backend = err?.response?.data?.error || err?.response?.data?.message;
        setError(backend || err?.message || 'Error al guardar tarea');
      }
    }
  };

  // UI RULING (Jaime, 2026-09-01 16:58): non-creators can no longer open the
  // edit form at all. This guards EVERY handleEdit call site (list buttons,
  // calendar chips, day-view chips), which also makes the edit series-scope
  // prompt (Solo esta / Todas) unreachable for non-creators.
  // FIX (2026-09-01 19:59): super_admin bypass removed from isCreator.
  const handleEdit = (task: Task) => {
    if (!isCreator(task)) return;
    setEditingId(task.id);
    editingTaskRef.current = task;
    setForm({
      title: task.title,
      category: task.category || 'other',
      assignedTo: taskAssignedTo(task),
      priority: task.priority || 'medium',
      status: task.status || 'pending',
      noteContent: taskNote(task),
      noteImage: '',
      noteImagePath: task.note_image_path ?? task.noteImagePath ?? null,
    });
    // Prefill the optional due-date controls from the task (date + optional
    // time from an ISO datetime, or date-only for plain dates).
    const raw = taskDue(task);
    if (raw) {
      const d = new Date(raw);
      if (!isNaN(d.getTime())) {
        const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(raw));
        const date = dateOnly ? raw : d.toISOString().split('T')[0];
        const time = dateOnly ? '' : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        // FEATURE (2026-09-01 15:28): prefill endDate (+ endTime when the
        // stored end_date carries a time component).
        const e = taskEnd(task);
        let endDate = '';
        let endTime = '';
        if (e) {
          const ed = new Date(e);
          if (!isNaN(ed.getTime())) {
            const eDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(e));
            endDate = eDateOnly ? e : ed.toISOString().split('T')[0];
            if (!eDateOnly) endTime = `${String(ed.getHours()).padStart(2, '0')}:${String(ed.getMinutes()).padStart(2, '0')}`;
          }
        }
        setDue({ hasDue: true, date, time, endDate, endTime });
      } else {
        setDue({ ...emptyDue });
      }
    } else {
      setDue({ ...emptyDue });
    }
    setRepeat(parseRepeatRule(taskRepeat(task)));
    setPendingScope(null);
    setShowForm(true);
  };

  const handleComplete = async (task: Task) => {
    try {
      await homeApi.tasks.update(task.id, { ...task, status: 'completed' }, activeUnitId);
      fetchTasks();
    } catch (err: any) {
      if (err?.response?.status !== 401) {
        // FIX (2026-09-01): same `error`-field extraction as handleSubmit —
        // any rejected status toggle must surface its backend reason.
        const backend = err?.response?.data?.error || err?.response?.data?.message;
        setError(backend || err?.message || 'Error al actualizar tarea');
      }
    }
  };

  // FEATURE (2026-09-01, Jaime ruling): series delete scope — deleting a task
  // that belongs to a series asks "¿Eliminar solo esta?" vs "¿Eliminar toda la
  // serie?" (same pattern as the existing edit scope prompt). Non-series tasks
  // keep the plain confirm and delete directly.
  const [deletingTask, setDeletingTask] = useState<Task | null>(null);

  // FEATURE (2026-09-22, Jaime scope correction): per-task Nota (<=2500 chars
  // + optional picture). Editing follows the page's creator-only edit rule
  // (same gate as Edit/Delete); everyone else sees the note read-only on the
  // card. Server contract: task PUT accepts noteContent (400 when >2500) and
  // noteImage (base64 data URL).
  const [noteOpenId, setNoteOpenId] = useState<string | null>(null);
  // FIX (2026-09-22 17:35, Jaime bug): read-only expansion for NON-creators
  // (they have no note editor). Creators clicking the compact indicator go
  // straight into the editor instead.
  const [noteExpandedId, setNoteExpandedId] = useState<string | null>(null);
  // RULING (Jaime, 2026-09-22): tapping the note picture opens an in-app
  // lightbox overlay instead of a raw target=_blank browser tab.
  const [noteLightbox, setNoteLightbox] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState<{ content: string; image: string }>({ content: '', image: '' });
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const noteFileRef = useRef<HTMLInputElement | null>(null);

  const openNote = (task: Task) => {
    if (!isCreator(task)) return;
    setNoteOpenId(task.id);
    setNoteDraft({ content: taskNote(task), image: taskNoteImage(task) || '' });
    setNoteError(null);
  };
  const closeNote = () => { setNoteOpenId(null); setNoteError(null); };
  // Hard-truncate paste/typing at the 2500-char limit (same ruling as Notas).
  const setNoteContent = (raw: string) => setNoteDraft(d => ({ ...d, content: raw.slice(0, NOTE_MAX_LEN) }));
  // Optional picture attach — same base64 data-URL flow used by pet photos.
  // FEATURE (2026-09-22, Jaime): create/edit form Nota fields (same data-URL
  // pipeline and 2500-char truncation as the per-card editor).
  const [formNoteError, setFormNoteError] = useState<string | null>(null);
  const formNoteFileRef = useRef<HTMLInputElement | null>(null);
  const setFormNoteContent = (raw: string) => setForm(f => ({ ...f, noteContent: raw.slice(0, NOTE_MAX_LEN) }));
  const pickFormNoteImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setFormNoteError('Usa una imagen JPG, PNG o WebP.');
      return;
    }
    setFormNoteError(null);
    // RULING (Jaime, 2026-09-22): compress before upload (max 1600px, JPEG
    // q0.8 = 150-400KB) instead of sending the raw multi-MB phone photo.
    compressImageToDataUrl(file, { maxDimension: 1600, quality: 0.8 })
      .then(dataUrl => setForm(f => ({ ...f, noteImage: dataUrl })))
      .catch(err => setFormNoteError(err instanceof Error ? err.message : 'No se pudo procesar la imagen.'));
  };

  const pickNoteImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setNoteError('Usa una imagen JPG, PNG o WebP.');
      return;
    }
    // RULING (Jaime, 2026-09-22): compress before upload (max 1600px, JPEG
    // q0.8 = 150-400KB) instead of sending the raw multi-MB phone photo.
    compressImageToDataUrl(file, { maxDimension: 1600, quality: 0.8 })
      .then(dataUrl => setNoteDraft(d => ({ ...d, image: dataUrl })))
      .catch(err => setNoteError(err instanceof Error ? err.message : 'No se pudo procesar la imagen.'));
  };
  const clearNoteImage = () => setNoteDraft(d => ({ ...d, image: '' }));
  const saveNote = async (task: Task, contentOverride?: string, imageOverride?: string) => {
    if (noteSaving) return;
    const content = (contentOverride ?? noteDraft.content).trim().slice(0, NOTE_MAX_LEN);
    const image = imageOverride !== undefined ? imageOverride : noteDraft.image;
    setNoteSaving(true);
    setNoteError(null);
    try {
      await homeApi.tasks.update(task.id, { ...task, noteContent: content, noteImage: image || null }, activeUnitId);
      setNoteOpenId(null);
      setNoteError(null);
      fetchTasks();
    } catch (err: any) {
      if (err?.response?.status !== 401) {
        const backend = err?.response?.data?.error || err?.response?.data?.message;
        setNoteError(backend || err?.message || 'Error al guardar la nota');
      }
    } finally {
      setNoteSaving(false);
    }
  };
  // FIX (2026-09-01, Jaime bug): deleting a task created by another user
  // silently did nothing — the backend 403 body carries the reason in an
  // `error` field (not `message`), so the UI fell through to a generic/absent
  // message. Now we surface the backend reason in Spanish and always close
  // the series-scope modal on failure so the still-existing task is visible.
  const deleteErrorMessage = (err: any): string => {
    const backend = err?.response?.data?.error || err?.response?.data?.message;
    if (backend && typeof backend === 'string') {
      if (backend.includes('Employees can only delete')) return 'No puedes eliminar esta tarea porque no fue creada por ti (solo el personal puede eliminar las tareas que creó).';
      if (backend.includes('Only the creator of the series')) return 'Solo quien creó la serie puede eliminar todas sus ocurrencias.';
      return `No se pudo eliminar la tarea: ${backend}`;
    }
    return 'No se pudo eliminar la tarea: esta tarea fue creada por otro usuario.';
  };
  const doDelete = async (id: string, scope?: 'single' | 'series') => {
    try {
      // FIX (2026-09-28, ticket #216): carry activeUnitId so requireUnit never 400s for multi-unit owners.
      await homeApi.tasks.delete(id, { ...(scope ? { scope } : {}), unitId: activeUnitId });
      setDeletingTask(null);
      fetchTasks();
    } catch (err: any) {
      // Suppress 401 errors — the interceptor handles auth redirects
      if (err?.response?.status !== 401) {
        setError(deleteErrorMessage(err));
      }
      // On failure the delete did NOT happen: close the scope modal so the
      // task remains visible next to the error instead of looking deleted
      // behind an open modal.
      setDeletingTask(null);
    }
  };
  const handleDelete = (task: Task) => {
    // UI RULING (Jaime, 2026-09-01 16:58): creator-only; also keeps the delete
    // series-scope modal (Eliminar solo esta / toda la serie) unreachable for
    // non-creators.
    if (!isCreator(task)) return;
    if (taskSeries(task)) { setDeletingTask(task); return; }
    if (!confirm('¿Eliminar esta tarea?')) return;
    void doDelete(task.id);
  };

  // FEATURE (2026-09-22, Jaime): compact ⋯ overflow menu on the card (mobile)
  // — Copiar / Editar / Eliminar — so long titles + notes render across the
  // full card width instead of being squeezed by inline action icons. Desktop
  // (md+) keeps the inline icons. "Copiar" duplicates the task (plain task:
  // same fields, status reset to pending; series materialization skipped).
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const handleDuplicate = async (task: Task) => {
    try {
      await homeApi.tasks.create({
        title: `${task.title} (copia)`,
        category: task.category || 'other',
        assignedTo: taskAssignedTo(task),
        priority: task.priority || 'medium',
        status: 'pending',
        dueDate: taskDue(task),
        endDate: taskEnd(task),
        noteContent: taskNote(task),
      }, activeUnitId);
      fetchTasks();
    } catch (err: any) {
      if (err?.response?.status !== 401) {
        const backend = err?.response?.data?.error || err?.response?.data?.message;
        setError(backend || err?.message || 'Error al duplicar tarea');
      }
    }
  };

  // FIX (2026-08-31): ownership/relatedness gates. isCreator drives Edit/Delete
  // visibility (FIX 2026-09-01 19:59: strictly taskCreatorId === currentUserId;
  // super_admin bypass removed so Edit/Delete are creator-only).
  // isRelated (creator OR assignee) drives the check-as-done toggle in employee
  // context, mirroring the backend getRelatedToUser ruling.
  const taskCreatorId = (t: Task) => String(t.user_id ?? t.userId ?? t.created_by ?? t.createdBy ?? '');
  const taskAssigneeId = (t: Task) => String(t.assignee_user_id ?? t.assigneeUserId ?? '');
  // FIX (2026-09-01): GET /tasks returns raw rows (assigned_to, snake_case),
  // but the page only read task.assignedTo (camelCase) — so a saved assignee
  // never rendered and edit-prefill silently dropped it (Jaime bug report).
  const taskAssignedTo = (t: Task) => String(t.assigned_to ?? t.assignedTo ?? '');
  const taskAssignedToAvatarUrl = (t: Task) => t.avatarUrl ?? t.avatar_url ?? null;
  const isCreator = (t: Task) => taskCreatorId(t) === currentUserId;
  const isRelated = (t: Task) => isCreator(t) || (currentUserId !== '' && taskAssigneeId(t) === currentUserId);

  // FEATURE (2026-09-01, Jaime): dated tasks first (closest upcoming first),
  // undated (plain to-dos) after. Defensive: missing fields must not crash.
  const isArchived = (t: Task) => !!(t.archived_at ?? t.archivedAt);
  const openTasks = tasks
    .filter(t => t.status !== 'completed' && !isArchived(t))
    .slice()
    .sort(byDueTs);
  const eod = endOfTodayTs();
  // FEATURE (2026-09-01, Jaime ruling): LIST view only shows open tasks due
  // within the next 2 weeks (end of day, local time). The backend keeps
  // materializing all recurring occurrences — later ones exist but are hidden
  // here. Overdue/today tasks stay visible; "Sin fecha" unchanged; completed
  // tasks are unaffected.
  const twoWeeksOutTs = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  })();
  const hiddenFutureCount = openTasks.filter(t => { const ts = dueTs(t); return ts !== null && ts > twoWeeksOutTs; }).length;
  const todayGroup = openTasks.filter(t => { const ts = dueTs(t); return ts !== null && ts <= eod; });
  const undatedGroup = openTasks.filter(t => dueTs(t) === null);
  const upcomingGroup = openTasks.filter(t => { const ts = dueTs(t); return ts !== null && ts > eod && ts <= twoWeeksOutTs; });
  const completedTasks = tasks
    .filter(t => t.status === 'completed' && !isArchived(t))
    .slice()
    .sort(byDueTs);
  const filtered = view === 'completadas'
    ? completedTasks
    : [...todayGroup, ...undatedGroup, ...upcomingGroup, ...(view === 'todas' ? completedTasks : [])];
  // Render rows with a group header on the first row of each group.
  const rows: TaskRow[] = view === 'completadas'
    ? withHeaders([['', completedTasks]])
    : withHeaders([
        ['Hoy', todayGroup],
        ['Sin fecha', undatedGroup],
        ['Próximas', upcomingGroup],
        ...(view === 'todas' ? [['Completadas', completedTasks] as [string, Task[]]] : []),
      ]);
  const pendingCount = tasks.filter(t => t.status === 'pending').length;
  const inProgressCount = tasks.filter(t => t.status === 'in_progress').length;
  const completedCount = tasks.filter(t => t.status === 'completed').length;

  // FEATURE (2026-09-01, Jaime): Calendario — bucket non-archived tasks by
  // due-date day key. Recurring occurrences each carry their own due_date so
  // they plot naturally. Tasks without a parsable due date are "not
  // plottable" and go to the always-visible "Sin fecha / sin hora" panel.
  const calTasksByDay = new Map<string, Task[]>();
  const calUndated: Task[] = [];
  for (const t of tasks) {
    if (isArchived(t)) continue;
    const key = dateKeyOf(t);
    if (!key) { calUndated.push(t); continue; }
    const arr = calTasksByDay.get(key);
    if (arr) arr.push(t); else calTasksByDay.set(key, [t]);
  }
  const calCells = monthGrid(calYear, calMonth);
  const dayPanelTasks = dayPanel ? (calTasksByDay.get(dayPanel) || []).slice().sort(byDueTs) : [];
  // FEATURE (2026-09-01, Jaime): Día view — tasks of the selected day split
  // into timed (plotted at their due hour) and all-day (date-only dues).
  const dayViewTasks = calTasksByDay.get(dayDate) || [];
  const dayViewAllDay = dayViewTasks.filter(t => hourOf(t) === null).sort(byDueTs);
  const dayViewByHour = new Map<number, Task[]>();
  for (const t of dayViewTasks) {
    const h = hourOf(t);
    if (h === null) continue;
    const arr = dayViewByHour.get(h);
    if (arr) arr.push(t); else dayViewByHour.set(h, [t]);
  }
  // 6:00–22:00 always visible; hours outside (0–5, 23) appear only when they
  // carry tasks so nothing can be hidden.
  const dayViewHours: number[] = [];
  for (let h = 0; h < 24; h++) if ((h >= 6 && h <= 22) || dayViewByHour.has(h)) dayViewHours.push(h);

  const renderUndatedItems = () => (
    calUndated.length === 0 ? (
      <p className="text-sm text-gray-400 dark:text-slate-500 py-3 text-center">Sin tareas sin fecha</p>
    ) : (
      <div className="space-y-2">
        {calUndated.slice().sort(byDueTs).map(task => (
          <button
            key={task.id}
            type="button"
            onClick={() => handleEdit(task)}
            title={task.title}
            className={`w-full text-left flex items-center gap-2 bg-[var(--v-surface-2)] px-2 py-1.5 rounded-lg hover:brightness-110 ${task.status === 'completed' ? 'opacity-50' : ''}`}
          >
            <Flag className={`w-3 h-3 flex-shrink-0 ${priorityColors[task.priority || 'medium']}`} />
            <span className={`text-sm text-[var(--v-text-primary)] dark:text-white truncate flex-1 ${task.status === 'completed' ? 'line-through' : ''}`}>{task.title}</span>
            {taskAssignedTo(task) && <Avatar name={taskAssignedTo(task)} avatarUrl={taskAssignedToAvatarUrl(task)} size={20} />}
          </button>
        ))}
      </div>
    )
  );

  // FEATURE (2026-09-01, Jaime): shared "Sin fecha / sin hora" panel — side
  // list on desktop, collapsible section on mobile (default unfolded). Used
  // by both the Calendario and the Día views.
  const undatedPanel = (
    <>
      <div className="hidden lg:block">
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400 mb-2">
            Sin fecha / sin hora ({calUndated.length})
          </p>
          {renderUndatedItems()}
        </div>
      </div>
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setUndatedOpen(o => !o)}
          className="w-full flex items-center justify-between bg-[var(--v-surface-1)] border border-[var(--v-surface-3)] rounded-xl px-4 py-3"
        >
          <span className="text-[var(--v-text-primary)] dark:text-white font-semibold text-sm">Sin fecha / sin hora ({calUndated.length})</span>
          <ChevronRight className={`w-4 h-4 text-gray-500 dark:text-slate-400 transition-transform ${undatedOpen ? 'rotate-90' : ''}`} />
        </button>
        {undatedOpen && <div className="mt-2">{renderUndatedItems()}</div>}
      </div>
    </>
  );

  return (
    <div className="space-y-6">
      <CreateHomeNudge />
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-gray-500 dark:text-slate-400 text-sm">Pendientes</p>
          <p className="text-2xl font-bold text-yellow-500 dark:text-yellow-400 mt-1">{pendingCount}</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-gray-500 dark:text-slate-400 text-sm">En progreso</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">{inProgressCount}</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-gray-500 dark:text-slate-400 text-sm">Completadas</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{completedCount}</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-500/50 text-red-700 dark:text-red-300 p-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => { setError(null); void fetchTasks(); }} className="ml-auto flex items-center gap-1 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"><RefreshCw className="w-4 h-4" />Reintentar</button>
          <button onClick={() => setError(null)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Filter + Add */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-2">
          {([
            ['pendientes', `Pendientes (${pendingCount + inProgressCount})`],
            ['completadas', `Completadas (${completedCount})`],
            ['todas', 'Todas'],
          ] as [ViewTab, string][]).map(([tab, label]) => (
            <button
              key={tab}
              onClick={() => setView(tab)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                view === tab ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
          {/* FEATURE (2026-09-01, Jaime): Lista | Día | Calendario view toggle.
              FIX: switching also clears the day popover so a leftover z-50
              backdrop can never swallow the next toggle click. */}
          <div className="flex gap-2">
            {([['lista', 'Lista'], ['dia', 'Día'], ['calendario', 'Calendario']] as [DisplayMode, string][]).map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => { setDisplayMode(mode); setDayPanel(null); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition border ${
                  displayMode === mode ? 'bg-blue-600 border-blue-600 text-white' : 'bg-transparent border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        <button
          onClick={() => { setForm(emptyForm); setDue({ ...emptyDue }); setRepeat({ ...emptyRepeat }); setEditingId(null); editingTaskRef.current = null; setShowForm(true); }}
          className="ml-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nueva Tarea
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-[var(--v-text-primary)] dark:text-white font-semibold">{editingId ? 'Editar Tarea' : 'Nueva Tarea'}</h3>
            <button onClick={() => { setShowForm(false); setEditingId(null); editingTaskRef.current = null; }} className="text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <input
            type="text"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="¿Qué necesita hacerse?"
            className="w-full bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-3 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
          />
          <div className="grid grid-cols-2 gap-3">
            <select
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            >
              <option value="cleaning">Limpieza</option>
              <option value="maintenance">Mantenimiento</option>
              <option value="shopping">Compras</option>
              <option value="other">Otro</option>
            </select>
            <select
              value={form.priority}
              onChange={e => setForm({ ...form, priority: e.target.value as Task['priority'] })}
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            >
              <option value="low">Prioridad Baja</option>
              <option value="medium">Prioridad Media</option>
              <option value="high">Prioridad Alta</option>
            </select>
            <select
              value={form.assignedTo}
              onChange={e => setForm({ ...form, assignedTo: e.target.value })}
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            >
              <option value="">Sin asignar</option>
              {user?.name && (
                <option value={user.name}>Mí ({user.name})</option>
              )}
              {unitMembers
                .filter(m => !user?.name || m.name !== user.name)
                .map(m => (
                  <option key={m.userId} value={m.name}>
                    {m.name}{memberRoleLabel(m.role) ? ` — ${memberRoleLabel(m.role)}` : ''}
                  </option>
                ))}
            </select>
          </div>
          {/* FEATURE (2026-09-01): optional due date — unchecked = plain to-do. */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={due.hasDue}
                onChange={e => setDue(e.target.checked ? { ...due, hasDue: true, date: due.date || new Date().toISOString().split('T')[0] } : { ...emptyDue })}
                className="w-4 h-4 accent-blue-600"
              />
              Con fecha límite
            </label>
            {due.hasDue && (
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="date"
                  value={due.date}
                  onChange={e => setDue({ ...due, date: e.target.value })}
                  className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
                />
                <input
                  type="time"
                  value={due.time}
                  onChange={e => setDue({ ...due, time: e.target.value })}
                  className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
                />
                <input
                  type="date"
                  title="Fecha de fin (opcional)"
                  value={due.endDate}
                  onChange={e => setDue({ ...due, endDate: e.target.value })}
                  className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
                />
                {/* FEATURE (2026-09-01 15:28): optional end time next to the
                    end date, mirroring the start-hour input. */}
                <input
                  type="time"
                  title="Hora de fin (opcional)"
                  value={due.endTime}
                  onChange={e => setDue({ ...due, endTime: e.target.value })}
                  className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
                />
              </div>
            )}
            {/* Repeat — requires a due date (server anchors recurrence on it). */}
            {due.hasDue && (
              <div className="space-y-2 border border-gray-200 dark:border-slate-700 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                  <RepeatIcon className="w-4 h-4" />
                  <span>Repetir</span>
                  <select
                    value={repeat.freq}
                    onChange={e => setRepeat({ ...repeat, freq: e.target.value as RepeatFreq })}
                    className="ml-auto bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="none">No repetir</option>
                    <option value="daily">Diario</option>
                    <option value="weekly">Semanal</option>
                    <option value="custom">Cada N días</option>
                  </select>
                </div>
                {repeat.freq === 'weekly' && (
                  <WeekdayPicker
                    selected={repeat.weekdays}
                    onToggle={(d) => setRepeat({
                      ...repeat,
                      weekdays: repeat.weekdays.includes(d)
                        ? repeat.weekdays.filter((w) => w !== d)
                        : [...repeat.weekdays, d],
                    })}
                  />
                )}
                {repeat.freq === 'custom' && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                      <span>Cada</span>
                      <input
                        type="number"
                        min={1}
                        value={repeat.interval}
                        onChange={e => setRepeat({ ...repeat, interval: e.target.value })}
                        className="w-20 bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-2 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
                      />
                      <span>días</span>
                    </div>
                    {/* Optional: restrict 'Cada N días' to specific weekdays (e.g. every 2 days, L/V only). */}
                    <div>
                      <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">Días de la semana (opcional)</div>
                      <WeekdayPicker
                        selected={repeat.weekdays}
                        onToggle={(d) => setRepeat({
                          ...repeat,
                          weekdays: repeat.weekdays.includes(d)
                            ? repeat.weekdays.filter((w) => w !== d)
                            : [...repeat.weekdays, d],
                        })}
                      />
                    </div>
                  </div>
                )}
                {repeat.freq !== 'none' && (
                  <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                    <span>Hasta (opcional)</span>
                    <input
                      type="date"
                      value={repeat.endDate}
                      onChange={e => setRepeat({ ...repeat, endDate: e.target.value })}
                      className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-1.5 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          {/* FEATURE (2026-09-01): series scope choice — shown once per save
              when the task belongs to a series (seriesId/recurrenceParentId). */}
          {editingId && editingTaskRef.current && taskSeries(editingTaskRef.current) && pendingScope === '' && (
            <div className="border border-blue-500/50 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 space-y-2">
              <p className="text-sm text-blue-700 dark:text-blue-200">Esta tarea pertenece a una serie. ¿Qué quieres editar?</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleSubmit('single')}
                  className="flex-1 py-2 rounded-lg font-medium text-sm bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600"
                >
                  Solo esta
                </button>
                <button
                  type="button"
                  onClick={() => handleSubmit('series')}
                  className="flex-1 py-2 rounded-lg font-medium text-sm bg-blue-600 text-white hover:bg-blue-700"
                >
                  Todas
                </button>
              </div>
            </div>
          )}
          {/* FEATURE (2026-09-22, Jaime): Nota opcional en el alta/edición —
              mismo contrato que el editor de la tarjeta (noteContent <=2500 +
              imagen data-URL en POST/PUT /home/tasks). */}
          <div className="space-y-2 border border-gray-200 dark:border-slate-700 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
              <StickyNote className="w-4 h-4" />
              <span>Nota (opcional)</span>
              <span className={`ml-auto text-xs ${form.noteContent.length >= NOTE_WARN_LEN ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-400 dark:text-slate-500'}`}>
                {form.noteContent.length}/{NOTE_MAX_LEN}
              </span>
            </div>
            {formNoteError && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-500/50 text-red-700 dark:text-red-300 p-2 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{formNoteError}</span>
              </div>
            )}
            <textarea
              rows={3}
              value={form.noteContent}
              onChange={e => setFormNoteContent(e.target.value)}
              placeholder="Añade una nota a esta tarea…"
              className="w-full bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none text-sm"
            />
            {form.noteImage && (
              <div className="relative inline-block">
                <img src={form.noteImage} alt="Nota" className="max-h-40 w-auto rounded-md border border-[var(--v-surface-3)]" />
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, noteImage: '' }))}
                  title="Quitar imagen"
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={formNoteFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={pickFormNoteImage}
              />
              <button
                type="button"
                onClick={() => formNoteFileRef.current?.click()}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600"
              >
                <ImagePlus className="w-4 h-4" />
                Imagen
              </button>
              {!!editingId && form.noteImagePath !== null && (
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, noteImagePath: '', noteImage: '' }))}
                  className="px-2.5 py-1.5 rounded-lg text-sm bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50"
                >
                  Quitar imagen guardada
                </button>
              )}
            </div>
          </div>
          <button
            onClick={() => handleSubmit(pendingScope || undefined)}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium"
          >
            {editingId ? 'Guardar Cambios' : 'Agregar Tarea'}
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500 dark:text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Cargando tareas...
        </div>
      )}

      {/* Task List (Lista view) */}
      {/* FIX LANE #166: neutral state when no unit is selected (multi-unit owner) */}
      {!loading && !activeUnitId && (
        <UnitPrompt />
      )}

      {!loading && !error && activeUnitId && displayMode === 'lista' && (
        <div className="space-y-3">
          {hiddenFutureCount > 0 && view !== 'completadas' && (
            <p className="text-xs text-gray-400 dark:text-slate-500 text-center">
              {hiddenFutureCount} {hiddenFutureCount === 1 ? 'tarea futura oculta' : 'tareas futuras ocultas'} (más de 14 días)
            </p>
          )}
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400 dark:text-slate-500">
              <p className="text-4xl mb-2">📋</p>
              <p>No hay tareas {view === 'completadas' ? 'completadas' : view === 'pendientes' ? 'pendientes' : ''}</p>
              {view !== 'completadas' && (
                <button
                  type="button"
                  onClick={() => { setForm(emptyForm); setDue({ ...emptyDue }); setRepeat({ ...emptyRepeat }); setEditingId(null); editingTaskRef.current = null; setShowForm(true); }}
                  className="mt-4 rounded-xl bg-[#36CCD8] px-4 py-2.5 text-sm font-semibold text-white"
                >
                  Crear tu primera tarea
                </button>
              )}
            </div>
          ) : (
            rows.map(({ task, header }) => (
              <Fragment key={task.id}>
                {header && (
                  <div className="pt-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400 flex items-center gap-2">
                      {header}
                      <span className="flex-1 h-px bg-[var(--v-surface-3)]" />
                    </p>
                  </div>
                )}
              <div
                className={`bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] flex items-start gap-3 ${task.status === 'completed' ? 'opacity-60' : ''}`}
              >
                {(!employeeContext || isRelated(task)) ? (
                  <button
                    type="button"
                    onClick={() => handleComplete(task)}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      task.status === 'completed' ? 'bg-green-500 border-green-500' : 'border-gray-300 dark:border-slate-500 hover:border-green-500'
                    }`}
                  >
                    {task.status === 'completed' && <Check className="w-4 h-4 text-white" />}
                  </button>
                ) : (
                  // Employee without relation to this task: read-only status dot
                  // (backend would 403 the status change).
                  <span
                    aria-hidden
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      task.status === 'completed' ? 'bg-green-500 border-green-500' : 'border-gray-300 dark:border-slate-600'
                    }`}
                  >
                    {task.status === 'completed' && <Check className="w-4 h-4 text-white" />}
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  <span className={`text-[var(--v-text-primary)] dark:text-white font-medium ${task.status === 'completed' ? 'line-through' : ''}`}>{task.title}</span>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {task.category && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${categoryColors[task.category] || categoryColors.other}`}>
                        {categoryLabels[task.category] || task.category}
                      </span>
                    )}
                    {taskDue(task) && (
                      <span className="text-gray-500 dark:text-slate-400 text-sm flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />{formatDueDate(taskDue(task))}
                      </span>
                    )}
                    {/* FEATURE (2026-09-01, Jaime): flag icon only — the
                        Baja/Media/Alta text label was removed from the card. */}
                    <span
                      title={priorityLabels[task.priority || 'medium']}
                      className={`text-sm flex items-center ${priorityColors[task.priority || 'medium']}`}
                    >
                      <Flag className="w-3 h-3" />
                    </span>
                    {/* FEATURE (2026-09-01, Jaime): assignee avatar chip (shared <Avatar>). */}
                    {taskAssignedTo(task) && (
                      <span title={taskAssignedTo(task)} className="flex-shrink-0">
                        <Avatar name={taskAssignedTo(task)} avatarUrl={taskAssignedToAvatarUrl(task)} size={24} />
                      </span>
                    )}
                  </div>
                  {/* FIX (2026-09-22 17:35, Jaime bug): the closed card must
                      show the note compactly — small h-10 thumbnail and/or a
                      ONE-LINE excerpt with the StickyNote icon, truncated so
                      long notes never push layout around. Clicking opens the
                      note editor (creator) or the expanded read-only view
                      (everyone else). Full note+image rendering stays inside
                      the editor / expanded view below. */}
                  {noteOpenId !== task.id && (taskNote(task) || taskNoteImage(task)) && (
                    /* FIX (2026-09-22 17:50, Jaime UX round 2): split the
                       compact row into two separate tap targets:
                       - thumbnail (or icon when no image) -> opens the
                         full-size lightbox directly (was swallowed by the
                         editor button — that was the "tap does nothing" bug);
                       - excerpt text -> opens the EXPANDED READ-ONLY view for
                         everyone (creator included). Editing stays behind the
                         ⋯ menu Nota item / the "Editar" button in the
                         expanded view, never as the default tap. */
                    <div
                      title="Ver nota"
                      className="mt-2 w-full flex items-center gap-2 text-left bg-[var(--v-surface-2)] rounded-lg p-1.5"
                    >
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          if (taskNoteImage(task)) setNoteLightbox(taskNoteImage(task)!);
                          else setNoteExpandedId(noteExpandedId === task.id ? null : task.id);
                        }}
                        title={taskNoteImage(task) ? 'Ver imagen' : 'Ver nota'}
                        className="flex-shrink-0 rounded"
                      >
                        {taskNoteImage(task) ? (
                          <img
                            src={taskNoteImage(task)!}
                            alt="Nota"
                            className="h-10 w-10 rounded object-cover border border-[var(--v-surface-3)]"
                          />
                        ) : (
                          <span className="h-10 w-10 rounded flex items-center justify-center bg-[var(--v-surface-1)] text-blue-600 dark:text-blue-400">
                            <StickyNote className="w-4 h-4" />
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); setNoteExpandedId(noteExpandedId === task.id ? null : task.id); }}
                        className="min-w-0 flex-1 text-left hover:opacity-80 transition-opacity"
                      >
                        {taskNote(task) && (
                          <span className="block text-sm text-gray-700 dark:text-slate-200 truncate">
                            {taskNote(task)}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400">
                          <StickyNote className="w-3 h-3 flex-shrink-0" />
                          Nota{taskNoteImage(task) ? ' · con imagen' : ''}
                        </span>
                      </button>
                    </div>
                  )}
{/* Expanded READ-ONLY note view (EVERYONE, creator included —
                      2026-09-22 Jaime: default tap must read, not edit) — full
                      untruncated text + image with lightbox; creator gets an
                      explicit Editar affordance. */}
                  {noteOpenId !== task.id && noteExpandedId === task.id && (
                    <div className="mt-2 bg-[var(--v-surface-2)] rounded-lg p-2.5 space-y-2">
                      {taskNote(task) && (
                        <p className="text-sm text-gray-700 dark:text-slate-200 whitespace-pre-wrap break-words">{taskNote(task)}</p>
                      )}
                      {taskNoteImage(task) && (
                        <button
                          type="button"
                          onClick={() => setNoteLightbox(taskNoteImage(task)!)}
                          title="Ver imagen"
                          className="block max-w-xs"
                        >
                          <img src={taskNoteImage(task)!} alt="Nota" className="max-h-40 w-auto rounded-md border border-[var(--v-surface-3)]" />
                        </button>
                      )}
                      <div className="flex items-center gap-3">
                        {isCreator(task) && (
                          <button
                            type="button"
                            onClick={() => { setNoteExpandedId(null); openNote(task); }}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            Editar
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setNoteExpandedId(null)}
                          className="text-xs text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                        >
                          Cerrar
                        </button>
                      </div>
                    </div>
                  )}
                  {/* FEATURE (2026-09-22, Jaime): Nota editor — creator-only,
                      same permission as Edit/Delete. Textarea with live
                      counter (red near the 2500 limit), optional picture with
                      preview, save / clear / cancel. */}
                  {noteOpenId === task.id && isCreator(task) && (
                    <div className="mt-2 space-y-2">
                      {noteError && (
                        <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-500/50 text-red-700 dark:text-red-300 p-2 rounded-lg flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm">{noteError}</span>
                        </div>
                      )}
                      <div className="relative">
                        <textarea
                          rows={3}
                          value={noteDraft.content}
                          onChange={e => setNoteContent(e.target.value)}
                          placeholder="Añade una nota a esta tarea…"
                          className="w-full bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-2 pr-16 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none text-sm"
                        />
                        <span
                          className={`absolute bottom-2 right-3 text-xs pointer-events-none ${noteDraft.content.length >= NOTE_WARN_LEN ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-400 dark:text-slate-500'}`}
                        >
                          {noteDraft.content.length}/{NOTE_MAX_LEN}
                        </span>
                      </div>
                      {noteDraft.image && (
                        <div className="relative inline-block">
                          <img src={noteDraft.image} alt="Nota" className="max-h-40 w-auto rounded-md border border-[var(--v-surface-3)]" />
                          <button
                            type="button"
                            onClick={clearNoteImage}
                            title="Quitar imagen"
                            className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          ref={noteFileRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={pickNoteImage}
                        />
                        <button
                          type="button"
                          onClick={() => noteFileRef.current?.click()}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600"
                        >
                          <ImagePlus className="w-4 h-4" />
                          Imagen
                        </button>
                        <button
                          type="button"
                          onClick={() => saveNote(task)}
                          disabled={noteSaving}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
                        >
                          {noteSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          Guardar nota
                        </button>
                        {(taskNote(task) || taskNoteImage(task)) && (
                          <button
                            type="button"
                            onClick={() => saveNote(task, '', '')}
                            disabled={noteSaving}
                            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-60"
                          >
                            Borrar nota
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={closeNote}
                          className="px-2 py-1.5 rounded-lg text-sm text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* UI RULING (Jaime, 2026-09-01 16:58): Edit AND Delete are
                      hidden from ANYONE who did not create the task — not just
                      employees. The Edit button previously rendered for
                      non-creators but silently did nothing on save (see
                      handleEdit guard), so both actions are creator-only.
                      strictly creator-only (super_admin bypass removed
                      2026-09-01 19:59). */}
                  {isCreator(task) && (
                    <>
                      {/* FEATURE (2026-09-22, Jaime): per-task Nota toggle.
                          UI TWEAK (2026-09-22, Jaime): on mobile the toggle
                          moved into the ⋯ overflow menu ("Nota"); desktop
                          (md+) keeps the inline icon per established pattern. */}
                      <button
                        type="button"
                        onClick={() => (noteOpenId === task.id ? closeNote() : openNote(task))}
                        title="Nota"
                        className={`hidden md:block p-2 ${noteOpenId === task.id || taskNote(task) || taskNoteImage(task) ? 'text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300' : 'text-gray-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400'}`}
                      >
                        <StickyNote className="w-4 h-4" />
                      </button>
                      {/* UI FIX (2026-09-22, Jaime, mobile): single ⋯ overflow
                          menu (Copiar/Editar/Eliminar) instead of inline icons
                          squeezing the title/note into a narrow column. */}
                      <div className="relative md:hidden">
                        {menuOpenId === task.id && (
                          <div className="fixed inset-0 z-10" onClick={() => setMenuOpenId(null)} />
                        )}
                        <button
                          type="button"
                          onClick={() => setMenuOpenId(menuOpenId === task.id ? null : task.id)}
                          title="Más acciones"
                          className="p-2 text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {menuOpenId === task.id && (
                          <div className="absolute right-0 top-full z-20 mt-1 w-44 rounded-lg border border-[var(--v-surface-3)] bg-[var(--v-surface-1)] shadow-lg py-1">
                            <button
                              type="button"
                              onClick={() => { setMenuOpenId(null); void handleDuplicate(task); }}
                              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-[var(--v-surface-2)] flex items-center gap-2"
                            >
                              <Copy className="w-4 h-4" /> Copiar
                            </button>
                            <button
                              type="button"
                              onClick={() => { setMenuOpenId(null); if (noteOpenId === task.id) closeNote(); else openNote(task); }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--v-surface-2)] flex items-center gap-2 ${noteOpenId === task.id || taskNote(task) || taskNoteImage(task) ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-slate-200'}`}
                            >
                              <StickyNote className="w-4 h-4" /> Nota
                            </button>
                            <button
                              type="button"
                              onClick={() => { setMenuOpenId(null); handleEdit(task); }}
                              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-[var(--v-surface-2)] flex items-center gap-2"
                            >
                              <Edit2 className="w-4 h-4" /> Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => { setMenuOpenId(null); handleDelete(task); }}
                              className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-[var(--v-surface-2)] flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" /> Eliminar
                            </button>
                          </div>
                        )}
                      </div>
                      {/* Desktop (md+): inline icons, space allows them. */}
                      <div className="hidden md:flex gap-1">
                        <button type="button" onClick={() => handleEdit(task)} className="text-gray-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 p-2">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => handleDelete(task)} className="text-gray-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-600 dark:text-red-400 p-2">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
              </Fragment>
            ))
          )}
        </div>
      )}

      {/* FEATURE (2026-09-01, Jaime): Día view — single-day agenda. Timed
          tasks plot at their due hour; date-only tasks go to the "Todo el
          día" row. Same chip styling / avatar / edit flow as the calendar
          (clicking a chip opens the same edit flow, series prompt included).
          The "Sin fecha / sin hora" panel stays visible here too. */}
      {!loading && !error && displayMode === 'dia' && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] p-3">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() => setDayDate(dateKeyShift(dayDate, -1))}
                className="p-2 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600"
                title="Día anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <p className="text-[var(--v-text-primary)] dark:text-white font-semibold capitalize">
                {fmtWeekday.format(new Date(dayDate + 'T12:00:00'))}, {fmtDate.format(new Date(dayDate + 'T12:00:00'))}
                {dayDate === todayKey() ? ' · Hoy' : ''}
              </p>
              <button
                type="button"
                onClick={() => setDayDate(dateKeyShift(dayDate, 1))}
                className="p-2 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600"
                title="Día siguiente"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setDayDate(todayKey())}
                className="ml-2 px-3 py-1.5 rounded-full text-sm bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600"
              >
                Hoy
              </button>
            </div>
            {dayViewAllDay.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400 mb-1">Todo el día</p>
                <div className="space-y-1">
                  {dayViewAllDay.map(task => (
                    <button
                      key={task.id}
                      type="button"
                      onClick={() => handleEdit(task)}
                      title={task.title}
                      className={`w-full text-left flex items-center gap-1 px-1 py-0.5 rounded ${categoryColors[task.category] || categoryColors.other} ${task.status === 'completed' ? 'opacity-50' : ''} hover:brightness-110`}
                    >
                      <Flag className={`w-2.5 h-2.5 flex-shrink-0 ${priorityColors[task.priority || 'medium']}`} />
                      <span className={`text-[11px] leading-tight truncate ${task.status === 'completed' ? 'line-through' : ''}`}>{task.title}</span>
                      {taskAssignedTo(task) && <Avatar name={taskAssignedTo(task)} avatarUrl={taskAssignedToAvatarUrl(task)} size={14} />}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {dayViewHours.map(h => {
              const hourTasks = (dayViewByHour.get(h) || []).sort(byDueTs);
              return (
                <div key={h} className="flex gap-2 items-start border-t border-[var(--v-surface-3)] min-h-[36px]">
                  <span className={`text-xs w-12 pt-1.5 flex-shrink-0 ${hourTasks.length ? 'text-blue-600 dark:text-blue-400 font-semibold' : 'text-gray-400 dark:text-slate-500'}`}>
                    {pad2(h)}:00
                  </span>
                  <div className="flex-1 py-1 space-y-1">
                    {hourTasks.length === 0 ? (
                      <span className="text-[11px] text-gray-300 dark:text-slate-600">—</span>
                    ) : (
                      hourTasks.map(task => (
                        <button
                          key={task.id}
                          type="button"
                          onClick={() => handleEdit(task)}
                          title={task.title}
                          className={`w-full text-left flex items-center gap-1 px-1 py-0.5 rounded ${categoryColors[task.category] || categoryColors.other} ${task.status === 'completed' ? 'opacity-50' : ''} hover:brightness-110`}
                        >
                          <Flag className={`w-2.5 h-2.5 flex-shrink-0 ${priorityColors[task.priority || 'medium']}`} />
                          <span className={`text-[11px] leading-tight truncate ${task.status === 'completed' ? 'line-through' : ''}`}>{task.title}</span>
                          {taskAssignedTo(task) && <Avatar name={taskAssignedTo(task)} avatarUrl={taskAssignedToAvatarUrl(task)} size={14} />}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
            {dayViewTasks.length === 0 && (
              <p className="text-sm text-gray-400 dark:text-slate-500 py-6 text-center">Sin tareas este día</p>
            )}
          </div>
          {undatedPanel}
        </div>
      )}

      {/* FEATURE (2026-09-01, Jaime): Calendario view — monthly grid + the
          always-visible "Sin fecha / sin hora" panel (side list on desktop,
          collapsible section below the grid on mobile). */}
      {!loading && !error && displayMode === 'calendario' && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] p-3">
            <div className="flex items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() => { const m = calMonth - 1; if (m < 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(m); }}
                className="p-2 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600"
                title="Mes anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <p className="text-[var(--v-text-primary)] dark:text-white font-semibold">{MONTHS_ES[calMonth]} {calYear}</p>
              <button
                type="button"
                onClick={() => { const m = calMonth + 1; if (m > 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(m); }}
                className="p-2 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600"
                title="Mes siguiente"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => { const n = new Date(); setCalYear(n.getFullYear()); setCalMonth(n.getMonth()); }}
                className="ml-2 px-3 py-1.5 rounded-full text-sm bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600"
              >
                Hoy
              </button>
            </div>
            <div className="grid grid-cols-7 gap-px bg-transparent">
              {CAL_WEEKDAYS.map((d, i) => (
                <div key={i} className="text-center text-xs font-semibold uppercase text-gray-500 dark:text-slate-400 pb-1">{d}</div>
              ))}
              {calCells.map(cell => {
                const dayTasks = (calTasksByDay.get(cell.key) || []).slice().sort(byDueTs);
                const visible = dayTasks.slice(0, 3);
                const extra = dayTasks.length - visible.length;
                return (
                  <div
                    key={cell.key}
                    className={`min-h-[84px] border border-[var(--v-surface-3)] p-1 space-y-1 ${cell.inMonth ? '' : 'opacity-40'} ${cell.isToday ? 'ring-1 ring-blue-500 bg-blue-50 dark:bg-blue-900/10' : ''}`}
                  >
                    <p className={`text-xs ${cell.isToday ? 'text-blue-600 dark:text-blue-400 font-bold' : 'text-gray-500 dark:text-slate-400'}`}>{cell.day}</p>
                    {visible.map(task => (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => handleEdit(task)}
                        title={task.title}
                        className={`w-full text-left flex items-center gap-1 px-1 py-0.5 rounded ${categoryColors[task.category] || categoryColors.other} ${task.status === 'completed' ? 'opacity-50' : ''} hover:brightness-110`}
                      >
                        <Flag className={`w-2.5 h-2.5 flex-shrink-0 ${priorityColors[task.priority || 'medium']}`} />
                        <span className={`text-[11px] leading-tight truncate ${task.status === 'completed' ? 'line-through' : ''}`}>{task.title}</span>
                        {taskAssignedTo(task) && <Avatar name={taskAssignedTo(task)} avatarUrl={taskAssignedToAvatarUrl(task)} size={14} />}
                      </button>
                    ))}
                    {extra > 0 && (
                      <button
                        type="button"
                        onClick={() => setDayPanel(cell.key)}
                        className="text-[11px] text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:text-blue-300 px-1"
                      >
                        +{extra} más
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Shared "Sin fecha / sin hora" panel (desktop side list + mobile
              collapsible) — same instance used by the Día view. */}
          {undatedPanel}
        </div>
      )}


      {/* FEATURE (2026-09-01, Jaime): day popover — full task list for a day
          with more than 3 chips on the calendar grid. Clicking a row opens the
          same edit modal as the list view. */}
      {dayPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setDayPanel(null)}>
          <div
            className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] w-full max-w-md space-y-3"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <p className="text-[var(--v-text-primary)] dark:text-white font-semibold">{fmtDate.format(new Date(dayPanel + 'T12:00:00'))}</p>
              <button type="button" onClick={() => setDayPanel(null)} className="text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {dayPanelTasks.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-slate-500 py-3 text-center">Sin tareas</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {dayPanelTasks.map(task => (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => { setDayPanel(null); handleEdit(task); }}
                    title={task.title}
                    className={`w-full text-left flex items-center gap-2 bg-[var(--v-surface-2)] px-2 py-1.5 rounded-lg hover:brightness-110 ${task.status === 'completed' ? 'opacity-50' : ''}`}
                  >
                    <Flag className={`w-3 h-3 flex-shrink-0 ${priorityColors[task.priority || 'medium']}`} />
                    <span className={`text-sm text-[var(--v-text-primary)] dark:text-white truncate flex-1 ${task.status === 'completed' ? 'line-through' : ''}`}>{task.title}</span>
                    {taskAssignedTo(task) && <Avatar name={taskAssignedTo(task)} avatarUrl={taskAssignedToAvatarUrl(task)} size={20} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete scope modal (series tasks): "¿Eliminar solo esta?" vs "¿Eliminar
          toda la serie?" — same pattern as the edit scope prompt. */}
      {deletingTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] w-full max-w-sm space-y-3">
            <p className="text-[var(--v-text-primary)] dark:text-white font-semibold">Esta tarea pertenece a una serie</p>
            <p className="text-sm text-gray-700 dark:text-slate-300">¿Qué quieres eliminar?</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => doDelete(deletingTask.id, 'single')}
                className="flex-1 py-2 rounded-lg font-medium text-sm bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600"
              >
                Solo esta
              </button>
              <button
                type="button"
                onClick={() => doDelete(deletingTask.id, 'series')}
                className="flex-1 py-2 rounded-lg font-medium text-sm bg-red-600 text-white hover:bg-red-700"
              >
                Toda la serie
              </button>
            </div>
            <button
              type="button"
              onClick={() => setDeletingTask(null)}
              className="w-full text-sm text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
      {/* RULING (Jaime, 2026-09-22): in-app lightbox overlay for the note
          picture — no raw target=_blank browser tab. Tap backdrop or X to close. */}
      {noteLightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setNoteLightbox(null)}
        >
          <div className="relative max-w-full max-h-full" onClick={e => e.stopPropagation()}>
            <img src={noteLightbox} alt="Nota" className="max-w-full max-h-[85vh] rounded-lg" />
            <button
              type="button"
              onClick={() => setNoteLightbox(null)}
              className="absolute -top-2 -right-2 bg-black/70 text-white rounded-full p-1.5 hover:bg-black/90"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
